import { ListCollection, Portal, Select, Spinner, useSlotRecipe } from '@chakra-ui/react';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { CustomIndicator } from '@components/ui/select/CustomIndicator';
import { MultiValueDisplay } from '@components/ui/select/MultiValueDisplay';
import { ComponentProps, forwardRef, ReactElement, Ref, useEffect, useRef, useState } from 'react';

import DefaultList from './DefaultList';

export type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

type Multiple =
  | {
      multiple: true;
      value: string[];
      defaultValue?: string[];
      clearable?: never;
      hideValue?: boolean;
      onChange: (value: string[]) => void;
    }
  | {
      multiple?: never;
      value?: string;
      defaultValue?: string;
      clearable?: boolean;
      hideValue?: boolean;
      onChange: (value: string | null) => void;
    };

type SelectProps = ComponentProps<typeof Select.Root>;
type SelectElementIds = NonNullable<SelectProps['ids']>;

export type CustomSelectProps<T extends Option> = {
  collection: ListCollection<T>;
  ids?: SelectElementIds;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  width?: number | string;
  listWidth?: number | string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'darker' | 'default';
  noOptionMessage?: string;
  formatLabel?: (value: T) => ReactElement;
  textStyle?: 'sm' | 'md' | 'lg';
} & Multiple;

function getDefaultPlaceholder() {
  return appIntl().formatMessage({ id: 'global.ellipsis' });
}

function CustomSelectInner<T extends Option>(
  {
    collection,
    value,
    defaultValue,
    disabled = false,
    loading = false,
    noOptionMessage,
    placeholder = getDefaultPlaceholder(),
    onChange,
    formatLabel,
    width,
    listWidth,
    label,
    size = 'md',
    textStyle = 'sm',
    hideValue = false,
    clearable = false,
    ...rest
  }: CustomSelectProps<T>,
  ref?: Ref<HTMLDivElement>,
) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const observer = new MutationObserver(() => {
      const attr = node.getAttribute('data-placement');
      if (attr?.startsWith('top')) {
        setPlacement('top');
      } else if (attr?.startsWith('bottom')) {
        setPlacement('bottom');
      }
    });

    observer.observe(node, {
      attributes: true,
      attributeFilter: ['data-placement'],
    });

    const attr = node.getAttribute('data-placement');
    if (attr?.startsWith('top')) setPlacement('top');
    else if (attr?.startsWith('bottom')) setPlacement('bottom');

    return () => observer.disconnect();
  }, []);

  const onValueChange = ({ value }: { value: string[] }) => {
    if (rest.multiple) {
      return (onChange as (value: string[]) => void)(value);
    } else {
      return (onChange as (value: string | null) => void)(value.length >= 1 ? value[0] : null);
    }
  };

  const getValue = () => {
    if (loading) {
      return <Spinner boxSize="16px" />;
    }
    if (hideValue) {
      return (
        <Select.ValueText textStyle={textStyle} placeholder={placeholder}>
          {label}
        </Select.ValueText>
      );
    }
    if (rest.multiple || Array.isArray(value)) {
      return <MultiValueDisplay placeholder={placeholder} />;
    } else if (formatLabel && !!value && collection.find(value)) {
      return formatLabel(collection.find(value)!);
    } else {
      return (
        <Select.ValueText textStyle={textStyle} placeholder={placeholder}>
          {collection.find(value)?.label ?? placeholder}
        </Select.ValueText>
      );
    }
  };

  const recipe = useSlotRecipe({ key: 'select' });
  const styles = recipe({ size });

  const isDisabled = disabled || loading;

  return (
    <Select.Root
      ref={ref}
      {...rest}
      size={size}
      width={width}
      disabled={isDisabled}
      className="group"
      collection={collection}
      value={Array.isArray(value) || value === undefined ? value : [value]}
      defaultValue={Array.isArray(defaultValue) || defaultValue === undefined ? defaultValue : [defaultValue]}
      onValueChange={onValueChange}
    >
      <Select.HiddenSelect />
      <Select.Control>
        {clearable && !isDisabled && !!value && (
          <Select.ClearTrigger
            position="absolute"
            top="50%"
            transform="translate(-50%, -50%)"
            right={styles.indicator['@layer recipes']?.width}
            onClick={() => onValueChange({ value: [] })}
          />
        )}
        <Select.Trigger>{getValue()}</Select.Trigger>
        <CustomIndicator portalPlacement={placement} size={size} disabled={isDisabled} />
      </Select.Control>
      <Portal>
        <Select.Positioner
          style={{
            marginTop: placement === 'bottom' ? '-6px' : '6px',
          }}
        >
          <Select.Content ref={contentRef} width={listWidth ? `${listWidth} !important` : undefined}>
            <DefaultList
              collection={collection}
              noOptionMessage={noOptionMessage}
              textStyle={textStyle}
              formatLabel={formatLabel}
            />
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}

// Cast the forwardRef to a generic-compatible component
export const CustomSelect = forwardRef(CustomSelectInner) as <T extends Option>(
  props: CustomSelectProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReturnType<typeof CustomSelectInner>;
